import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";

const API_BASE = process.env.REACT_APP_URL;
const authConfig = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` }
});

export default function ViewInputPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const [darkMode, setDarkMode] = useState(false);

  useEffect(() => {
    const syncTheme = () => {
      const savedMode = localStorage.getItem("darkMode");
      setDarkMode(savedMode ? JSON.parse(savedMode) : false);
    };
    syncTheme();
    window.addEventListener("themeChanged", syncTheme);
    return () => window.removeEventListener("themeChanged", syncTheme);
  }, []);

  // Normalize output for records from either Mongo (nested `output`) or
  // Prisma/Postgres (flattened columns like `output_label`, `output_confidence`, etc.)
  const normalizeOutput = (record) => {
    if (!record) return null;

    if (record.output) {
      try {
        return typeof record.output === "string" ? JSON.parse(record.output) : record.output;
      } catch {
        return record.output;
      }
    }

    // Prisma flattened fields
    const label = record.output_label ?? undefined;
    const confidence = record.output_confidence ?? undefined;
    const plagScore = record.output_plagScore ?? undefined;
    const activeTab = record.output_activeTab ?? undefined;

    // reconstruct possible plagMatches from mapped fields (output_plagMatches_0_phrase...)
    const plagMatches = [];
    for (let i = 0; i < 20; i++) {
      const phraseKey = `output_plagMatches_${i}_phrase`;
      const titleKey = `output_plagMatches_${i}_title`;
      const linkKey = `output_plagMatches_${i}_link`;
      if (record[phraseKey]) {
        plagMatches.push({ phrase: record[phraseKey], title: record[titleKey], link: record[linkKey] });
      }
    }

    return {
      label: label ?? undefined,
      confidence: confidence ?? undefined,
      plagScore: plagScore ?? undefined,
      activeTab: activeTab ?? undefined,
      plagMatches: plagMatches.length > 0 ? plagMatches : undefined,
    };
  };

  useEffect(() => {
    const fetchFeedback = async () => {
      try {
        const res = await axios.get(`${API_BASE}/feedback/${id}`, authConfig());
        console.log("Fetched data:", res.data);

        const normalizedOutput = normalizeOutput(res.data);
        setData({ ...res.data, output: normalizedOutput });
      } catch (err) {
        console.error("Error fetching feedback input:", err);
        setError("Unable to load feedback input.");
      } finally {
        setLoading(false);
      }
    };

    fetchFeedback();
  }, [id]);

  if (loading) {
    return <h3 style={{ textAlign: "center", marginTop: "80px" }}>Loading...</h3>;
  }

  if (error) {
    return <h3 style={{ textAlign: "center", color: "red", marginTop: "80px" }}>{error}</h3>;
  }

  if (!data) {
    return <h3 style={{ textAlign: "center", marginTop: "80px" }}>No data found.</h3>;
  }

  const output = data.output;
  const label = output?.label ?? "—";
  const confidence = output?.confidence ?? "—";
  const plagScore = output?.plagScore ?? "—";
  const plagMatches = output?.plagMatches ?? [];

  const isHuman = label === "Human Written";
  const boxColor = isHuman ? "#22c55e" : "#ef4444";
  const bgColor = isHuman ? "#e7f9ed" : "#fde8e8";
  const plagColor = (plagScore !== "—" && plagScore > 0) ? "#ef4444" : "#22c55e";
  const plagBgColor = (plagScore !== "—" && plagScore > 0) ? "#fde8e8" : "#e7f9ed";

  return (
    <div
      className={darkMode ? "dark-mode" : ""}
      style={{
        fontFamily: "Poppins, sans-serif",
        textAlign: "center",
        padding: "100px 40px 250px",
        minHeight: "100vh",
        background: darkMode ? "transparent" : "linear-gradient(135deg, #f4f2f8, #eae6f3)",
        transition: "background 0.3s, color 0.3s",
      }}
    >
      <button
        onClick={() => navigate(-1)}
        style={{
          backgroundColor: "#636CCB",
          color: "white",
          border: "none",
          borderRadius: "8px",
          padding: "10px 18px",
          fontSize: "0.95em",
          cursor: "pointer",
          marginBottom: "25px",
          position: "fixed",
          top: "75px",
          left: "30px",
          boxShadow: "0 3px 8px rgba(0,0,0,0.2)",
          transition: "transform 0.2s ease",
          zIndex: 1000,
        }}
        onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
        onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
      >
        ⮜ Back
      </button>

      <h2 style={{ color: "var(--text-main)", marginBottom: "15px" }}>
        {data.inputType === "file" ? "Uploaded File Content" : "User Text Input"}
      </h2>

      <div
        style={{
          backgroundColor: "var(--card-bg)",
          borderRadius: "12px",
          padding: "20px",
          maxWidth: "1200px",
          margin: "0 auto",
          textAlign: "left",
          whiteSpace: "pre-wrap",
          color: "var(--text-main)",
          boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
          fontSize: "1rem",
          transition: "background 0.3s, color 0.3s",
        }}
      >
        {data.input || "No text available."}
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "20px",
          flexWrap: "wrap",
          margin: "30px auto 10px",
          maxWidth: "900px",
        }}
      >
        <div
          style={{
            backgroundColor: darkMode ? "rgba(34, 197, 94, 0.1)" : bgColor,
            border: `2px solid ${boxColor}`,
            borderRadius: "10px",
            padding: "15px 25px",
            flex: "1",
            minWidth: "250px",
            boxShadow: "0 3px 8px rgba(0,0,0,0.1)",
          }}
        >
          <h3 style={{ color: boxColor, marginBottom: "8px" }}>AI Output {label}</h3>
          <p style={{ color: "var(--text-main)", fontWeight: "500", fontSize: "1.1em" }}>
            Confidence: <span style={{ color: boxColor }}>{confidence}%</span>
          </p>
        </div>

        <div
          style={{
            backgroundColor: darkMode ? "rgba(239, 68, 68, 0.1)" : plagBgColor,
            border: `2px solid ${plagColor}`,
            borderRadius: "10px",
            padding: "15px 25px",
            flex: "1",
            minWidth: "250px",
            boxShadow: "0 3px 8px rgba(0,0,0,0.1)",
          }}
        >
          <h3 style={{ color: plagColor, marginBottom: "8px" }}>
            Plagiarism: {plagScore !== "—" ? `${plagScore}%` : "—"}
          </h3>
          <p style={{ color: "var(--text-main)", fontWeight: "500", fontSize: "1.1em" }}>
            Status: <span style={{ color: plagColor }}>
              {plagScore !== "—" && plagScore > 0 ? "Matches Found" : "Unique Content"}
            </span>
          </p>
        </div>
      </div>

      {plagMatches && plagMatches.length > 0 && (
        <div
          style={{
            maxWidth: "900px",
            margin: "20px auto",
            textAlign: "left",
            backgroundColor: "var(--card-bg)",
            padding: "20px",
            borderRadius: "12px",
            boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
            transition: "background 0.3s, color 0.3s",
          }}
        >
          <h4 style={{ color: "var(--text-main)", marginTop: 0, marginBottom: "15px" }}>Plagiarism Matches ({plagScore}%):</h4>
          {plagMatches.map((m, i) => (
            <div key={i} style={{
              marginBottom: "12px",
              padding: "10px",
              backgroundColor: "var(--zebra-stripe)",
              borderRadius: "8px",
              borderLeft: "4px solid #ef4444"
            }}>
              <p style={{ margin: "0 0 5px 0", fontSize: "0.9em", fontStyle: "italic", color: "var(--text-main)" }}>"{m.phrase}"</p>
              <a
                href={`${m.link}#:~:text=${(() => {
                  const words = m.phrase.split(/\s+/);
                  if (words.length > 8) {
                    const start = words.slice(0, 4).join(' ');
                    const end = words.slice(-4).join(' ');
                    return `${encodeURIComponent(start)},${encodeURIComponent(end)}`;
                  }
                  return encodeURIComponent(m.phrase);
                })()}`}
                target="_blank"
                rel="noreferrer"
                style={{ color: "#636CCB", fontSize: "0.8em", fontWeight: "600", textDecoration: "none" }}
              >
                Source Link ↗
              </a>
            </div>
          ))}
        </div>
      )}

      {/* User Comment Section */}
      {data.comment && (
        <div
          style={{
            maxWidth: "900px",
            margin: "20px auto",
            textAlign: "left",
            backgroundColor: "var(--card-bg)",
            padding: "20px",
            borderRadius: "12px",
            boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
            transition: "background 0.3s, color 0.3s",
          }}
        >
          <h4 style={{ color: "var(--text-main)", marginTop: 0, marginBottom: "12px", display: "flex", alignItems: "center", gap: "8px" }}>
            Comment
          </h4>
          <p style={{ margin: 0, fontSize: "0.95em", color: "var(--text-main)", lineHeight: "1.6" }}>
            {data.comment}
          </p>
        </div>
      )}

      <p style={{ marginTop: "20px", fontSize: "0.9em", color: "var(--text-sub)" }}>
        Submitted by <strong>{data.username}</strong> on{" "}
        {new Date(data.createdAt).toLocaleString()}
      </p>
    </div>
  );
}